import React, { useState, useEffect } from "react";
import {
  TextInput,
  Table,
  Pagination,
  Flex,
  Text,
  Alert,
  Button,
  Card,
  Stack,
  Group,
  Divider,
  Menu,
} from "@mantine/core";
import { PageContainer } from "../components/Container/PageContainer";
import {
  IconSearch,
  IconAlertCircle,
  IconDownload,
  IconFileCode,
  IconInfoCircle,
  IconChevronDown,
  IconBookmark,
  IconBookmarkFilled,
  IconDotsVertical,
  IconMail,
} from "@tabler/icons-react";
import { ActionIcon, Tooltip } from "@mantine/core";
import DataLoader from "../components/DataLoader";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../utils/analytics";
import { useThreadsState, useThreadsDispatch, loadLifetimeDataWithTimeline } from "../contexts/ThreadsContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { useUser } from "../contexts/UserContext";
import DateRangeSelector from "../components/DateRangeSelector";
import ThreadPreviewModal from "../components/ThreadPreviewModal";
import { fetchAndCompareThreads } from "../utils/threadDateRangeUpdate";
import { dbService } from "../firebaseInstance";
import EmailExtractionModal from "../components/EmailExtractionModal";

const SearchThreads = () => {
  const { lifetimeThreadsList, isLoading } = useThreadsState();
  const dispatch = useThreadsDispatch();
  const { profile } = useUser();
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredThreads, setFilteredThreads] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const threadsPerPage = 30;
  const [sortConfig, setSortConfig] = useState({
    key: "timestamp",
    direction: "desc", // 최근 날짜부터 표시
  });
  const isMobile = useIsMobile("xl");

  // Date range update states
  const [isDateRangeLoading, setIsDateRangeLoading] = useState(false);
  const [dateRangeProgress, setDateRangeProgress] = useState(null);
  const [previewThreads, setPreviewThreads] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [updateResult, setUpdateResult] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Bookmark states
  const [bookmarkList, setBookmarkList] = useState([]);
  const [isBookmarkLoading, setIsBookmarkLoading] = useState(false);

  // Email extraction states
  const [emailExtractionModal, setEmailExtractionModal] = useState({
    opened: false,
    threadId: null,
    threadText: ""
  });

  const handleDownloadCSV = () => {
    if (!filteredThreads || filteredThreads.length === 0) {
      console.log("No data to download.");
      // Optionally show a notification to the user
      return;
    }

    // Define CSV Headers based on columns, including mobile ones for full data export
    const headers = [
      "Date",
      "Content",
      "Views",
      "Likes",
      "Replies",
      "Reposts",
      "ER (%)", // Engagement Rate
      "EA", // Engagement Amount
      "Link",
    ];
    const csvRows = [headers.join(",")]; // Add header row

    // Create CSV rows from filteredThreads data
    filteredThreads.forEach((thread) => {
      const timestamp = new Date(thread.timestamp).toLocaleDateString();
      // Escape commas and quotes in text content
      // Also remove the Unicode Replacement Character (U+FFFD)
      const textContent = `"${thread.text
        .replace(/"/g, '""')
        .replace(/\uFFFD/g, "")}"`;
      const views = thread.views || 0;
      const likes = thread.likes || 0;
      const replies = thread.replies || 0;
      const reposts = thread.reposts || 0;
      const engagementRate = views
        ? (((likes + replies + reposts) / views) * 100).toFixed(2)
        : "0.00";
      const engagementAmount = likes + replies + reposts;
      const permalink = thread.permalink || "";

      const row = [
        timestamp,
        textContent,
        views,
        likes,
        replies,
        reposts,
        engagementRate,
        engagementAmount,
        permalink,
      ].join(",");
      csvRows.push(row);
    });

    const csvString = csvRows.join("\n");
    // Add BOM for Excel to properly recognize UTF-8 encoding
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "threads_export.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    trackEvent("download_csv", { count: filteredThreads.length });
    console.log("CSV download triggered.");
  };

  const handleDownloadJSON = () => {
    if (!filteredThreads || filteredThreads.length === 0) {
      console.log("No data to download.");
      // Optionally show a notification to the user
      return;
    }

    // Prepare data for JSON export
    const jsonData = filteredThreads.map((thread) => {
      const views = thread.views || 0;
      const likes = thread.likes || 0;
      const replies = thread.replies || 0;
      const reposts = thread.reposts || 0;
      const engagementRate = views
        ? parseFloat((((likes + replies + reposts) / views) * 100).toFixed(2))
        : 0;
      const engagementAmount = likes + replies + reposts;

      return {
        date: new Date(thread.timestamp).toLocaleDateString(),
        content: thread.text.replace(/\uFFFD/g, ""), // Remove replacement character
        views: views,
        likes: likes,
        replies: replies,
        reposts: reposts,
        engagementRate: engagementRate,
        engagementAmount: engagementAmount,
        link: thread.permalink || "",
      };
    });

    const jsonString = JSON.stringify(jsonData, null, 2); // Pretty print JSON
    // Add BOM for proper UTF-8 encoding recognition
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + jsonString], {
      type: "application/json;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "threads_export.json");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    trackEvent("download_json", { count: filteredThreads.length });
    console.log("JSON download triggered.");
  };

  const sortThreads = (threads) => {
    if (!sortConfig.key) return threads;

    return [...threads].sort((a, b) => {
      if (sortConfig.key === "timestamp") {
        const timestampA = new Date(a.timestamp).getTime();
        const timestampB = new Date(b.timestamp).getTime();
        return sortConfig.direction === "asc"
          ? timestampA - timestampB
          : timestampB - timestampA;
      }
      if (
        sortConfig.key === "engagementRate" ||
        sortConfig.key === "engagementAmount"
      ) {
        const valA =
          sortConfig.key === "engagementRate"
            ? ((a.likes + a.replies + a.reposts) / a.views) * 100
            : a.likes + a.replies + a.reposts;
        const valB =
          sortConfig.key === "engagementRate"
            ? ((b.likes + b.replies + b.reposts) / b.views) * 100
            : b.likes + b.replies + b.reposts;
        return sortConfig.direction === "asc" ? valA - valB : valB - valA;
      }
      if (sortConfig.direction === "asc") {
        return a[sortConfig.key] - b[sortConfig.key];
      }
      return b[sortConfig.key] - a[sortConfig.key];
    });
  };

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
    trackEvent("sort_table", { key: key });
  };

  // Load bookmarks from Firestore subcollection
  useEffect(() => {
    const loadBookmarks = async () => {
      if (!profile?.id) return;

      try {
        const bookmarksSnapshot = await dbService
          .collection("user")
          .doc(profile.id)
          .collection("bookmarkedThreads")
          .get();

        const bookmarkIds = bookmarksSnapshot.docs.map(doc => doc.id);
        setBookmarkList(bookmarkIds);
      } catch (error) {
        console.error("Failed to load bookmarks:", error);
      }
    };

    loadBookmarks();
  }, [profile?.id]);

  // Toggle bookmark
  const toggleBookmark = async (threadId) => {
    if (!profile?.id || isBookmarkLoading) return;

    setIsBookmarkLoading(true);
    try {
      const bookmarkRef = dbService
        .collection("user")
        .doc(profile.id)
        .collection("bookmarkedThreads")
        .doc(threadId);

      const isBookmarked = bookmarkList.includes(threadId);

      if (isBookmarked) {
        // Remove bookmark - delete document
        await bookmarkRef.delete();
        setBookmarkList(bookmarkList.filter(id => id !== threadId));
        trackEvent("toggle_bookmark", { threadId, action: "remove" });
      } else {
        // Add bookmark - create document with thread data
        const thread = lifetimeThreadsList.find(t => t.id === threadId);
        if (thread) {
          await bookmarkRef.set({
            id: thread.id,
            text: thread.text,
            timestamp: thread.timestamp,
            views: thread.views || 0,
            likes: thread.likes || 0,
            replies: thread.replies || 0,
            reposts: thread.reposts || 0,
            permalink: thread.permalink,
            media_url: thread.media_url || null,
            media_type: thread.media_type || null,
            bookmarkedAt: new Date().toISOString(),
          });
          setBookmarkList([...bookmarkList, threadId]);
          trackEvent("toggle_bookmark", { threadId, action: "add" });
        }
      }
    } catch (error) {
      console.error("Failed to toggle bookmark:", error);
    } finally {
      setIsBookmarkLoading(false);
    }
  };

  useEffect(() => {
    if (!lifetimeThreadsList) return;
    console.log(searchQuery);

    if (searchQuery.length > 5) {
      trackEvent("search", { query: searchQuery });
    }

    const filtered = lifetimeThreadsList.filter(
      (thread) =>
        thread.text &&
        thread.text.toLowerCase().includes(searchQuery.toLowerCase())
    );
    const sorted = sortThreads(filtered);
    setFilteredThreads(sorted);
    setCurrentPage(1);
  }, [searchQuery, lifetimeThreadsList, sortConfig]);

  const indexOfLastThread = currentPage * threadsPerPage;
  const indexOfFirstThread = indexOfLastThread - threadsPerPage;
  const currentThreads = filteredThreads.slice(
    indexOfFirstThread,
    indexOfLastThread
  );

  const { t } = useTranslation();

  // Date range update handler
  const handleFetchThreads = async (startDate, endDate) => {
    if (!profile?.id) {
      console.error("User profile not found");
      return;
    }

    setIsDateRangeLoading(true);
    setUpdateResult(null);
    setPreviewThreads([]);

    try {
      const result = await fetchAndCompareThreads(
        profile.id,
        startDate,
        endDate,
        (progress) => {
          setDateRangeProgress(progress);
          console.log("Progress:", progress);
        }
      );

      if (result.success && result.threads.length > 0) {
        // Show preview modal with fetched threads
        setPreviewThreads(result.threads);
        setShowPreviewModal(true);
      } else if (result.success && result.threads.length === 0) {
        // No new threads found
        setUpdateResult(result);
        setShowPreviewModal(true);
      } else {
        // Error occurred
        setUpdateResult(result);
        setShowPreviewModal(true);
      }
    } catch (error) {
      console.error("Failed to fetch threads:", error);
      setUpdateResult({
        success: false,
        error: error.message,
        totalFetched: 0,
        uniqueAdded: 0,
        duplicatesSkipped: 0
      });
      setShowPreviewModal(true);
    } finally {
      setIsDateRangeLoading(false);
      setDateRangeProgress(null);
    }
  };

  // Confirm update handler
  const handleConfirmUpdate = async (selectedThreads) => {
    setIsUpdating(true);
    
    try {
      // Update threads to Firestore
      const { updateThreadsToFirestore } = await import("../utils/threadDateRangeUpdate");
      await updateThreadsToFirestore(profile.id, selectedThreads);
      
      // Update result
      const result = {
        success: true,
        totalFetched: previewThreads.length,
        uniqueAdded: selectedThreads.length,
        duplicatesSkipped: previewThreads.length - selectedThreads.length
      };
      
      setUpdateResult(result);
      
      // Refresh lifetime data immediately (sharding system handles sync)
      if (selectedThreads.length > 0) {
        loadLifetimeDataWithTimeline(dispatch, profile.id, profile);
      }
    } catch (error) {
      console.error("Failed to update threads:", error);
      setUpdateResult({
        success: false,
        error: error.message,
        totalFetched: previewThreads.length,
        uniqueAdded: 0,
        duplicatesSkipped: 0
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCloseModal = () => {
    setShowPreviewModal(false);
    setPreviewThreads([]);
    setUpdateResult(null);
  };

  // Open email extraction modal
  const handleOpenEmailExtraction = (thread) => {
    setEmailExtractionModal({
      opened: true,
      threadId: thread.id,
      threadText: thread.text
    });
    trackEvent("open_email_extraction", { threadId: thread.id });
  };

  // Close email extraction modal
  const handleCloseEmailExtraction = () => {
    setEmailExtractionModal({
      opened: false,
      threadId: null,
      threadText: ""
    });
  };

  const columns = [
    { key: "views", label: t("searchThreads.tableColumns.views") },
    {
      key: "likes",
      label: t("searchThreads.tableColumns.likes"),
      mobileHidden: true,
    },
    {
      key: "replies",
      label: t("searchThreads.tableColumns.replies"),
      mobileHidden: true,
    },
    {
      key: "reposts",
      label: t("searchThreads.tableColumns.reposts"),
      mobileHidden: true,
    },
    {
      key: "engagementRate",
      label: t("searchThreads.tableColumns.engagementRate"),
      mobileHidden: true,
    },
    {
      key: "engagementAmount",
      label: t("searchThreads.tableColumns.engagementAmount"),
      mobileHidden: true,
    },
  ];

  return (
    <PageContainer>
      <DataLoader msg={t("loading.lifetime")} isLoad={isLoading} />
      <Stack gap="sm">
        <Flex direction="row" justify="space-between" align="center" gap="md">
          <TextInput
            style={{ flexGrow: 1 }} // Make TextInput take available space
            placeholder={t("searchThreads.terminology.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftSection={<IconSearch size={24} />}
            size={"xl"}
          />
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <Button
                leftSection={<IconDownload size={18} />}
                rightSection={<IconChevronDown size={16} />}
                disabled={!filteredThreads || filteredThreads.length === 0}
                size="xl"
                color="inform.9"
              >
                Download
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconDownload size={16} />}
                onClick={handleDownloadCSV}
              >
                {t("searchThreads.terminology.downloadCsv")}
              </Menu.Item>
              <Menu.Item
                leftSection={<IconFileCode size={16} />}
                onClick={handleDownloadJSON}
              >
                {t("searchThreads.terminology.downloadJson")}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>

        {/* Date Range Selector - Added between search bar and engagement rate info */}
        <DateRangeSelector 
          onFetchThreads={handleFetchThreads}
          isLoading={isDateRangeLoading}
        />

                 <Group gap="lg">
           <Text size="lg">
             {t("searchThreads.terminology.engagementRate")}: {t("searchThreads.terminology.engagementRateDesc")}
           </Text>
           <Text size="lg">
             {t("searchThreads.terminology.engagementAmount")}: {t("searchThreads.terminology.engagementAmountDesc")}
           </Text>
         </Group>

        {currentThreads.length === 0 ? (
          <Text>{t("searchThreads.terminology.noResults")}</Text>
        ) : (
          <>
            <Table highlightOnHover fz={isMobile ? "md" : "lg"} withTableBorder={false} withColumnBorders={false} withRowBorders={false} style={{ borderRadius: "8px", overflow: "hidden" }}>
              <Table.Thead style={{
                display: "block",
                width: "100%",
                scrollbarGutter: "stable",
                backgroundColor: "var(--mantine-color-inform-9)",
                color: "white"
              }}>
                <Table.Tr style={{ display: "flex", width: "100%" }}>
                  <Table.Th
                    p={isMobile ? 8 : 16}
                    style={{ flex: "0 0 60px", color: "white", textAlign: "center" }}
                  >
                    <IconBookmark size={20} />
                  </Table.Th>
                  <Table.Th
                    p={isMobile ? 8 : 16}
                    style={{ flex: "0 0 120px", cursor: "pointer", color: "white" }}
                    onClick={() => handleSort("timestamp")}
                  >
                    <Flex direction="row" align="center">
                      {t("searchThreads.tableColumns.date")}
                      {sortConfig.key === "timestamp" && (
                        <span>
                          {sortConfig.direction === "asc" ? " ↑" : " ↓"}
                        </span>
                      )}
                    </Flex>
                  </Table.Th>
                  <Table.Th p={isMobile ? 8 : 16} style={{ flex: "1", color: "white" }}>
                    <Flex direction="row" align="center">
                      {t("searchThreads.tableColumns.content")}
                    </Flex>
                  </Table.Th>
                  {columns.map((col) =>
                    !isMobile || !col.mobileHidden ? (
                      <Table.Th
                        key={col.key}
                        p={isMobile ? 8 : 16}
                        style={{ flex: "0 0 100px", cursor: "pointer", color: "white" }}
                        onClick={() => handleSort(col.key)}
                      >
                        <Flex direction="row" align="center">
                          {col.label}
                          {sortConfig.key === col.key && (
                            <span>
                              {sortConfig.direction === "asc" ? " ↑" : " ↓"}
                            </span>
                          )}
                        </Flex>
                      </Table.Th>
                    ) : null
                  )}
                  <Table.Th
                    p={isMobile ? 8 : 16}
                    style={{ flex: "0 0 60px", color: "white", textAlign: "center" }}
                  >
                    {/* Actions column */}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody
                style={{
                  display: "block",
                  overflow: "auto",
                  height: isMobile
                    ? "calc(100vh - 280px - 24px)"
                    : "calc(100vh - 280px - 48px)",
                  width: "100%",
                  backgroundColor: "white"
                }}
              >
                {currentThreads.map((thread) => (
                  <Table.Tr
                    key={thread.id}
                    style={{ display: "flex", width: "100%" }}
                  >
                    <Table.Td
                      p={isMobile ? 8 : 16}
                      style={{ flex: "0 0 60px", textAlign: "center" }}
                    >
                      <Tooltip label={bookmarkList.includes(thread.id) ? "Remove bookmark" : "Add bookmark"}>
                        <ActionIcon
                          variant="subtle"
                          color={bookmarkList.includes(thread.id) ? "yellow.6" : "gray.6"}
                          onClick={() => toggleBookmark(thread.id)}
                          disabled={isBookmarkLoading}
                          size="md"
                        >
                          {bookmarkList.includes(thread.id) ? (
                            <IconBookmarkFilled size={20} />
                          ) : (
                            <IconBookmark size={20} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td
                      p={isMobile ? 8 : 16}
                      style={{ flex: "0 0 120px" }}
                    >
                      {new Date(thread.timestamp).toLocaleDateString()}
                    </Table.Td>
                    <Table.Td p={isMobile ? 8 : 16} style={{ flex: "1" }}>
                      <a
                        href={thread.permalink}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {thread.text.split(".")[0].replace(/�/g, "")}
                      </a>
                    </Table.Td>
                    <Table.Td
                      p={isMobile ? 8 : 16}
                      style={{ flex: "0 0 100px" }}
                    >
                      {thread.views}
                    </Table.Td>
                    {!isMobile && (
                      <>
                        <Table.Td
                          p={isMobile ? 8 : 16}
                          style={{ flex: "0 0 100px" }}
                        >
                          {thread.likes}
                        </Table.Td>
                        <Table.Td
                          p={isMobile ? 8 : 16}
                          style={{ flex: "0 0 100px" }}
                        >
                          {thread.replies}
                        </Table.Td>
                        <Table.Td
                          p={isMobile ? 8 : 16}
                          style={{ flex: "0 0 100px" }}
                        >
                          {thread.reposts}
                        </Table.Td>
                        <Table.Td
                          p={isMobile ? 8 : 16}
                          style={{ flex: "0 0 100px" }}
                        >
                          {thread.views && thread.views > 0
                            ? (
                                ((thread.likes + thread.replies + thread.reposts) /
                                  thread.views) *
                                100
                              ).toFixed(2)
                            : "0.00"}
                          %
                        </Table.Td>
                        <Table.Td
                          p={isMobile ? 8 : 16}
                          style={{ flex: "0 0 100px" }}
                        >
                          {thread.likes + thread.replies + thread.reposts}
                        </Table.Td>
                      </>
                    )}
                    <Table.Td
                      p={isMobile ? 8 : 16}
                      style={{ flex: "0 0 60px", textAlign: "center" }}
                    >
                      <Menu shadow="md" width={200}>
                        <Menu.Target>
                          <ActionIcon
                            variant="subtle"
                            color="gray.6"
                            size="md"
                          >
                            <IconDotsVertical size={20} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconMail size={16} />}
                            onClick={() => handleOpenEmailExtraction(thread)}
                          >
                            이메일 추출
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Flex justify="center" w="100%">
              <Pagination
                size="sm"
                total={Math.ceil(filteredThreads.length / threadsPerPage)}
                value={currentPage}
                onChange={setCurrentPage}
              />
            </Flex>
          </>
        )}
      </Stack>

      {/* Thread Preview Modal */}
      <ThreadPreviewModal
        opened={showPreviewModal}
        onClose={handleCloseModal}
        threads={previewThreads}
        onConfirmUpdate={handleConfirmUpdate}
        updateResult={updateResult}
        isUpdating={isUpdating}
      />

      {/* Email Extraction Modal */}
      <EmailExtractionModal
        opened={emailExtractionModal.opened}
        onClose={handleCloseEmailExtraction}
        threadId={emailExtractionModal.threadId}
        threadText={emailExtractionModal.threadText}
      />
    </PageContainer>
  );
};

export default SearchThreads;
